import { Box, SxProps, Theme, Tooltip } from '@mui/material';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import LightboxBackgroundImage from '../../LightboxBackgroundImage';
import { cornerChipSx, fillMediaSx, SingleItemContainer, SingleItemMedia, SkuTileName } from '../Card.styles';
import { QuantityChip } from './QuantityChip';

interface IProps {
  imageUrl: string;
  name?: string;
  quantity?: number;
  sx?: SxProps<Theme>;
  className?: string;
}

export const CardImage: React.FC<IProps> = ({ imageUrl, name, quantity, sx, className }) => {
  const isQuantityShown = (quantity ?? 0) > 1;
  const nameRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el) {
      return;
    }
    setIsTruncated(el.scrollWidth > el.clientWidth + 1);
  }, [name]);

  const columnSx: SxProps<Theme> = useMemo(() => ({ display: 'flex', flexDirection: 'column', gap: 1, ...sx }), [sx]);

  // The caption row is always rendered so cards keep the same height with or without a name.
  const nameNode = (
    <SkuTileName ref={nameRef} aria-hidden={!name}>
      {name || '\u00a0'}
    </SkuTileName>
  );

  return (
    <Box sx={columnSx}>
      <SingleItemContainer className={className}>
        {isQuantityShown && <QuantityChip quantity={quantity ?? 0} sx={cornerChipSx} />}
        <SingleItemMedia>
          <LightboxBackgroundImage
            url={imageUrl}
            asImage
            alt={name || 'Product image'}
            isScalable={false}
            sx={fillMediaSx}
          />
        </SingleItemMedia>
      </SingleItemContainer>
      {isTruncated ? (
        <Tooltip title={name} placement="top" arrow>
          {nameNode}
        </Tooltip>
      ) : (
        nameNode
      )}
    </Box>
  );
};
